import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import { Controller } from 'react-hook-form';
import { useEffect } from 'react';
import { Skeleton } from 'src/components/ui/skeleton';
import { clsx } from 'clsx';
import { API_VERSION } from 'src/constants/api-routes';
import type { PersonaFieldProps } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/personaFieldProps';
import { getApiVersion } from 'src/utils/apiVersion';

// Keyed by the field's option keys.
export const TONE_OF_VOICE_STYLE_EMOJI: Record<string, string> = {
  casual: '👋',
  professional: '💼️',
  custom: '🌟',
};

export const ToneOfVoiceStyleField = ({
  field,
  data,
  control,
  readonly,
  setValue,
  watch,
}: PersonaFieldProps<'pf_tone_of_voice_style'>) => {
  const fieldValue = watch(field.key);
  const version = getApiVersion();
  const isV2 = version === API_VERSION.V2;

  useEffect(() => {
    if (!fieldValue && data) {
      setValue(field.key, data);
      return;
    }

    if (readonly) {
      if (data) {
        setValue(field.key, data);
      } else if (field?.default_value?.value) {
        setValue(field.key, field?.default_value?.value);
      } else {
        setValue(field.key, '');
      }
    }
  }, [readonly, data, field]);

  return field ? (
    <div className={'flex flex-col gap-2.5'}>
      <div>
        <p className={'text-ebony text-sm font-medium'}>Style</p>
      </div>
      <div>
        {field?.options?.length ? (
          <Controller
            control={control}
            name={field.key}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <ToggleGroup
                className={clsx(
                  'flex gap-1.5 justify-items-stretch items-stretch w-full',
                  readonly && 'pointer-events-none',
                )}
                onValueChange={onChange}
                defaultValue={value}
                value={value}
                type="single"
              >
                {field.options.map((option) => {
                  const isCustom = option.value === 'custom';

                  if (isV2 && isCustom) {
                    return null;
                  }

                  return (
                    <ToggleGroupItem
                      className={'flex flex-1 items-center justify-center'}
                      key={option.key}
                      value={option.value}
                      onClick={() => {
                        setValue('pf_tone_of_voice_emoji', option.key !== 'professional');
                      }}
                    >
                      {TONE_OF_VOICE_STYLE_EMOJI[option.key]
                        ? `${TONE_OF_VOICE_STYLE_EMOJI[option.key]} ${option.value}`
                        : option.value}
                    </ToggleGroupItem>
                  );
                })}
              </ToggleGroup>
            )}
          />
        ) : (
          <div className={'flex flex-row gap-[3px]'}>
            <Skeleton className={'w-1/3 h-11'} />
            <Skeleton className={'w-1/3 h-11'} />
            <Skeleton className={'w-1/3 h-11'} />
          </div>
        )}
      </div>
    </div>
  ) : null;
};
